/**
 * Phone numbers are stored as E.164 strings only (`+15551234567`). Every entry point (manual entry,
 * imports, intake, webhooks) normalizes with `normalizePhone` before anything is stored or compared.
 *
 * The market is almost entirely US, so North American numbers get a strict shape check (10 digits,
 * area code starting 2–9). The exchange is not checked so the fictional 555 numbers used by tests and
 * the fake provider (`+1555000000X`) stay valid. Other countries are accepted only when typed with a
 * leading `+` and a plausible length; they are not validated further.
 */

/** Matches the `phone` check constraints in the database. */
export const E164_PATTERN = /^\+[1-9]\d{6,14}$/;

export type PhoneResult = { ok: true; e164: string } | { ok: false; reason: string };

const NANP_PATTERN = /^[2-9]\d{9}$/;

export function normalizePhone(input: string): PhoneResult {
  const trimmed = input.trim();
  if (trimmed === '') return { ok: false, reason: 'Enter a phone number' };
  // Letters (vanity numbers, "ext. 12") are not something we can text reliably.
  if (/[a-z]/i.test(trimmed)) {
    return { ok: false, reason: 'Use digits only (no letters or extensions)' };
  }
  if (!/^[\d\s().+-]+$/.test(trimmed)) {
    return { ok: false, reason: 'Use digits, spaces, dashes, dots or brackets only' };
  }
  const plus = trimmed.startsWith('+') || trimmed.startsWith('00');
  let digits = trimmed.replace(/\D/g, '');
  if (trimmed.startsWith('00')) digits = digits.slice(2);
  if (trimmed.indexOf('+') > 0) return { ok: false, reason: 'A + may only start the number' };

  if (plus && !digits.startsWith('1')) {
    if (digits.length < 7 || digits.length > 15 || digits.startsWith('0')) {
      return { ok: false, reason: 'That international number has the wrong length' };
    }
    return { ok: true, e164: `+${digits}` };
  }

  // North American Numbering Plan: optional country code 1, then 10 digits.
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  if (digits.length !== 10) {
    return { ok: false, reason: 'A US number has 10 digits (area code + number)' };
  }
  if (!NANP_PATTERN.test(digits)) {
    return { ok: false, reason: 'A US area code cannot start with 0 or 1' };
  }
  return { ok: true, e164: `+1${digits}` };
}

export function isE164(value: string): boolean {
  return E164_PATTERN.test(value);
}

/** Display form: `(555) 123-4567` for North American numbers, the E.164 string otherwise. */
export function formatPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}
